import './JobCard.css';
import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LaptopIcon from '@mui/icons-material/Laptop';
import BusinessIcon from '@mui/icons-material/Business';
import { NavLink } from 'react-router-dom';

interface ButtonConfig {
  backgroundColor?: string;
  backgroundColorOnHover?: string;
  text: string;
}

interface JobCardProps {
  jobTitle: string;
  location: string;
  employmentType?: string;
  jobId: number;
  buttonConfig?: ButtonConfig;
  remote?: boolean;
  isStatusCard?: boolean;
  isIncomplete?: boolean;
}

const JobCard: React.FC<JobCardProps> = ({
  jobTitle,
  location,
  employmentType,
  jobId,
  buttonConfig,
  remote = false,
  isStatusCard = false,
  isIncomplete = false,
}) => {
  return (
    <Card sx={{ mb: 4 }}>
      <CardContent className="jobCardContent">
        <Grid container spacing={1} alignItems="center">
          {/* Title Section */}
          <Grid item xs={12} sm={8} container direction="row">
            <Grid item>
              <Typography
                variant="h5"
                sx={{ 
                  textAlign: 'left', 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.25rem', sm: '1.25rem', md: '1.5rem' }
                }}
              >
                {jobTitle}
              </Typography>
            </Grid>
            {isStatusCard && isIncomplete ? (
              <Grid item>
                <NavLink to={`/assessments/${jobId}`}>
                  <Button
                    variant="text"
                    sx={{ marginLeft: '20px', textDecoration: 'underline' }}
                  >
                    Continue Your Application
                  </Button>
                </NavLink>
              </Grid>
            ) : null}
          </Grid>

          {/* Button Section - Desktop only */}
          <Grid item xs={12} sm={4} container justifyContent="flex-end" sx={{ display: { xs: 'none', sm: 'flex' } }}>
            {isStatusCard ? (
              isIncomplete ? (
                <NavLink to={`/assessments/${jobId}`}>
                  <Button
                    className="viewDetailsButton"
                    variant="contained"
                    sx={{
                      backgroundColor: 'var(--ttg-black)',
                      '&:hover': {
                        backgroundColor: 'var(--ttg-green)',
                      },
                      marginTop: { sm: '10px', md: '20px' },
                      padding: { sm: '6px 12px', md: '10px 20px' },
                      fontSize: { sm: '0.75rem', md: '0.875rem' },
                      minWidth: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {'Continue Assessment'}
                  </Button>
                </NavLink>
              ) : (
                <Button
                  className="viewDetailsButton"
                  variant="contained"
                  sx={{
                    backgroundColor:
                      buttonConfig?.backgroundColor || 'var(--ttg-black)',
                    '&:hover': {
                      backgroundColor:
                        buttonConfig?.backgroundColorOnHover ||
                        'var(--ttg-green)',
                    },
                    marginTop: { sm: '10px', md: '20px' },
                    padding: { sm: '6px 12px', md: '10px 20px' },
                    fontSize: { sm: '0.75rem', md: '0.875rem' },
                    cursor: 'auto',
                    width: { sm: 'auto', md: '250px' },
                    minWidth: 'auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {buttonConfig?.text || 'View Details'}
                </Button>
              )
            ) : (
              <NavLink to={`/jobs/${jobId}`}>
                <Button
                  className="viewDetailsButton"
                  variant="contained"
                  sx={{
                    backgroundColor: 'var(--ttg-black)',
                    '&:hover': {
                      backgroundColor: 'var(--ttg-green)',
                    },
                    marginTop: { sm: '10px', md: '20px' },
                    padding: { sm: '6px 12px', md: '10px 20px' },
                    fontSize: { sm: '0.75rem', md: '0.875rem' },
                    minWidth: 'auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {'View Details'}
                </Button>
              </NavLink>
            )}
          </Grid>

          {/* Metadata Section - Desktop with icons */}
          <Grid
            item
            container
            xs={12}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-start' }}
          >
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <LocationOnIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
                <Typography 
                  variant="body2" 
                  color="var(--ttg-black)" 
                  textAlign="left"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {location}
                </Typography>
              </Box>
            </Grid>

            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {remote ? (
                  <LaptopIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
                ) : (
                  <BusinessIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
                )}
                <Typography 
                  variant="body2" 
                  color="var(--ttg-black)" 
                  textAlign="left"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {remote ? 'Remote' : 'On-site'}
                </Typography>
              </Box>
            </Grid>

            {employmentType ? (
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <AccessTimeIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
                  <Typography 
                    variant="body2" 
                    color="var(--ttg-black)" 
                    textAlign="left"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {employmentType}
                  </Typography>
                </Box>
              </Grid>
            ) : null}

          </Grid>

          {/* Metadata Section - Mobile with separators */}
          <Grid item xs={12} sx={{ display: { xs: 'block', sm: 'none' }, mb: 1 }}>
            <Typography 
              variant="body2" 
              color="var(--ttg-black)" 
              sx={{ fontSize: '0.75rem', textAlign: 'left' }}
            >
              {location} | {remote ? 'Remote' : 'On-site'} | {employmentType ? `${employmentType} | ` : ''}
            </Typography>
          </Grid>

          {/* Button Section - Mobile only */}
          <Grid item xs={12} container justifyContent="flex-start" sx={{ display: { xs: 'flex', sm: 'none' } }}>
            {isStatusCard ? (
              isIncomplete ? (
                <NavLink to={`/assessments/${jobId}`}>
                  <Button
                    className="viewDetailsButton"
                    variant="contained"
                    sx={{
                      backgroundColor: 'var(--ttg-black)',
                      '&:hover': {
                        backgroundColor: 'var(--ttg-green)',
                      },
                      marginTop: '5px',
                      padding: '6px 12px',
                      fontSize: '0.7rem',
                      minWidth: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {'Continue Assessment'}
                  </Button>
                </NavLink>
              ) : (
                <Button
                  className="viewDetailsButton"
                  variant="contained"
                  sx={{
                    backgroundColor:
                      buttonConfig?.backgroundColor || 'var(--ttg-black)',
                    '&:hover': {
                      backgroundColor:
                        buttonConfig?.backgroundColorOnHover ||
                        'var(--ttg-green)',
                    },
                    marginTop: '5px',
                    padding: '6px 12px',
                    fontSize: '0.7rem',
                    cursor: 'auto',
                    width: 'auto',
                    minWidth: 'auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {buttonConfig?.text || 'View Details'}
                </Button>
              )
            ) : (
              <NavLink to={`/jobs/${jobId}`}>
                <Button
                  className="viewDetailsButton"
                  variant="contained"
                  sx={{
                    backgroundColor: 'var(--ttg-black)',
                    '&:hover': {
                      backgroundColor: 'var(--ttg-green)',
                    },
                    marginTop: '5px',
                    padding: '6px 12px',
                    fontSize: '0.7rem',
                    minWidth: 'auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {'View Details'}
                </Button>
              </NavLink>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default JobCard;
export { JobCardProps };
